import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Edit, Image as ImageIcon, Users, Search, Plus, CheckCircle, Plane, TrendingUp, Cloud, Sprout, Wallet, Hotel, FileText, Copy, ExternalLink } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getDadosCliente } from "@/services/mockDB";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

const CompanyDetail: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("dados-cadastrais");
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [mudasApoiadas, setMudasApoiadas] = useState(0);

  // Busca mudas apoiadas pelos clientes da empresa
  useEffect(() => {
    const calcularMudasApoiadas = () => {
      // Para Aviões S.A., o cliente é João (id "1")
      const clientId = "1";
      const cliente = getDadosCliente(clientId);
      
      // Soma saídas ligadas a mudas / 6 (R$ 6,00 = 1 muda)
      const transacoes = cliente?.transacoes || [];
      const totalDoadoMudas = transacoes
        .filter(t => t.tipoTransacao === 'saida' && (t.tipo === 'Apoio a Mudas' || (t.tipo && t.tipo.includes('Mudas'))))
        .reduce((acc, curr) => acc + (Number(Math.abs(curr.valor)) || 0), 0);

      setMudasApoiadas(Math.floor(totalDoadoMudas / 6.0));
    };

    calcularMudasApoiadas();

    // Listener para atualizações do localStorage
    const handleStorageUpdate = () => {
      calcularMudasApoiadas();
    };

    window.addEventListener('mockDB:updated', handleStorageUpdate);
    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('mockDB:updated', handleStorageUpdate);
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  // Função para copiar para clipboard
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copiado!",
      description: `${label} copiado para a área de transferência.`,
    });
  };
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false);
  const [checkInForm, setCheckInForm] = useState({
    clienteId: "",
    cidadeOrigem: "",
    meioTransporte: "",
    distancia: ""
  });

  // Mock data - em produção viria de uma API baseado no companyId
  const companiesData: Record<string, any> = {
    "1": {
      id: "1",
      razaoSocial: "Aviões S.A.",
      nomeFantasia: "Aviões",
      cnpj: "06.164.253/0001-87",
      ramoNegocio: "Transporte Aéreo",
      cupomProprio: "AVIOESMSC10",
      status: "Ativo",
      logo: "/images/avioes.png"
    },
    "hotel-mar-brasil": {
      id: "hotel-mar-brasil",
      razaoSocial: "Hotel Mar do Brasil Ltda",
      nomeFantasia: "Hotel Mar",
      cnpj: "12.345.678/0001-90",
      ramoNegocio: "Hotelaria",
      cupomProprio: "HOTELMAR15",
      status: "Ativo",
      logo: "/images/hotel.png"
    }
  };

  const companyData = companiesData[companyId || "1"] || companiesData["1"];
  const company = {
    id: companyData.id,
    razaoSocial: companyData.razaoSocial,
    nomeFantasia: companyData.nomeFantasia,
    cnpj: companyData.cnpj,
    ramoNegocio: companyData.ramoNegocio,
    cupomProprio: companyData.cupomProprio,
    status: companyData.status,
    logo: companyData.logo
  };

  const [searchTerm, setSearchTerm] = useState("");

  // Função para calcular emissão de CO2 baseado no meio de transporte
  const calcularEmissaoCO2 = (distancia: number, meioTransporte: string): number => {
    const fatoresEmissao: Record<string, number> = {
      "Carro": 0.19, // kg CO2/km
      "Ônibus": 0.05, // kg CO2/km
      "Aéreo Convencional": 0.25 // kg CO2/km
    };
    return distancia * (fatoresEmissao[meioTransporte] || 0);
  };

  // Função para calcular moedas baseado na emissão
  const calcularMoedas = (emissaoCO2: number): number => {
    const taxaConversao = 10; // 10 Moedas por kg de CO2
    return emissaoCO2 * taxaConversao;
  };

  // Função para processar check-in
  const handleCheckInSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!checkInForm.clienteId || !checkInForm.cidadeOrigem || !checkInForm.meioTransporte || !checkInForm.distancia) {
      toast({
        title: "Erro",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    const distancia = parseFloat(checkInForm.distancia);
    if (isNaN(distancia) || distancia <= 0) {
      toast({
        title: "Erro",
        description: "A distância deve ser um número válido maior que zero.",
        variant: "destructive"
      });
      return;
    }

    // Calcular emissão e moedas
    const emissaoCO2 = calcularEmissaoCO2(distancia, checkInForm.meioTransporte);
    const moedas = calcularMoedas(emissaoCO2);

    // Aqui seria feita a chamada à API para salvar no banco de dados
    // Por enquanto, apenas simulamos o sucesso
    console.log("Check-in registrado:", {
      empresa_id: companyId,
      usuario_id: checkInForm.clienteId,
      tipo: "Hospedagem",
      cidade_origem: checkInForm.cidadeOrigem,
      meio_transporte: checkInForm.meioTransporte,
      distancia: distancia,
      emissao_co2: emissaoCO2,
      valor_moeda: moedas,
      status: "Aprovado"
    });

    // Exibir feedback de sucesso
    toast({
      title: "Check-in registrado com sucesso!",
      description: `O Hotel compensou ${emissaoCO2.toFixed(3)} kg de CO₂ e o cliente recebeu ${moedas.toFixed(2)} Moedas.`,
    });

    // Limpar formulário e fechar modal
    setCheckInForm({
      clienteId: "",
      cidadeOrigem: "",
      meioTransporte: "",
      distancia: ""
    });
    setIsCheckInModalOpen(false);
  };

  // Mock data para clientes
  const clients = [
    { 
      id: "1", 
      nomeCompleto: "João Silva", 
      cpf: "123.456.789-00", 
      email: "joao.silva@email.com",
      telefone: "(11) 98765-4321",
      localizacao: "São Paulo, Brasil",
      tipo: "PF",
      dataCadastro: "15/01/2024",
      status: "Ativo" 
    }
  ];

  const filteredClients = clients.filter(client =>
    client.nomeCompleto.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.cpf.includes(searchTerm) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Dados dos clientes com suas viagens e check-ins (em produção viria de uma API)
  // Baseado nos dados reais do cliente João Silva
  const clientsData: Record<string, any> = {
    "1": {
      id: "1",
      viagens: [
        { 
          id: "1", 
          data: "20/12/2025", 
          rota: "SDU - GRU", 
          distancia: "366 km", 
          emissao: "60 kg",
          valorPassagem: 1500.00,
          cashbackTotal: 45.00
        }
      ],
      checkIns: [
        { id: "1", data: "05/03/2024", origem: "Brasília, DF", transporte: "Aéreo Convencional", distancia: 1060, emissao: 265.0, moedas: 2650.0 },
        { id: "2", data: "18/03/2024", origem: "Curitiba, PR", transporte: "Carro", distancia: 410, emissao: 77.9, moedas: 779.0 },
        { id: "3", data: "28/03/2024", origem: "Porto Alegre, RS", transporte: "Ônibus", distancia: 1550, emissao: 77.5, moedas: 775.0 }
      ]
    }
  };

  // Função para calcular cashback baseado na emissão de CO2 (50 MSC por kg)
  const calcularCashback = (emissaoStr: string): number => {
    const emissaoNum = parseFloat(emissaoStr.replace(" kg", "").replace(",", ".").trim());
    if (isNaN(emissaoNum)) return 0;
    return emissaoNum * 50;
  };

  // Calcular métricas agregadas baseadas nos dados reais dos clientes
  const isAviation = company.ramoNegocio === "Transporte Aéreo";
  
  // Para Aviões S.A., o cliente é João (id "1")
  // Para Hotel Mar, também usamos o mesmo cliente mas com check-ins
  const companyClientIds = isAviation ? ["1"] : ["1"]; // Em produção viria de uma API
  
  let totalViagens = 0;
  let totalCheckIns = 0;
  let totalDistancia = 0;
  let totalCO2Kg = 0;
  let totalCashbacksGerados = 0;

  companyClientIds.forEach(clientId => {
    const clientData = clientsData[clientId];
    const cliente = getDadosCliente(clientId);

    // Total de cashbacks gerados: soma ENTRADAS de cashback de viagem no extrato do cliente (mockDB/localStorage)
    const transacoes = cliente?.transacoes || [];
    totalCashbacksGerados += transacoes
      .filter(t => t.tipoTransacao === 'entrada' && t.tipo === 'Entrada' && t.destino === 'Cashback de Viagem')
      .reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);

    if (clientData) {
      if (isAviation && clientData.viagens) {
        // Para aviação: contar viagens e somar distâncias/emissões
        totalViagens += clientData.viagens.length;
        clientData.viagens.forEach((viagem: any) => {
          // Remove " km" e pontos de milhar, mantém apenas números
          const distanciaStr = viagem.distancia.replace(" km", "").replace(/\./g, "").trim();
          totalDistancia += parseFloat(distanciaStr) || 0;
          
          const emissaoStr = viagem.emissao.replace(" kg", "").replace(",", ".").trim();
          const emissaoKg = parseFloat(emissaoStr) || 0;
          totalCO2Kg += emissaoKg;
        });
      } else if (!isAviation && clientData.checkIns) {
        // Para hotelaria: contar check-ins e somar distâncias/emissões
        totalCheckIns += clientData.checkIns.length;
        clientData.checkIns.forEach((checkIn: any) => {
          totalDistancia += checkIn.distancia || 0;
          totalCO2Kg += checkIn.emissao || 0;
        });
      }
    }
  });

  // Converter CO2 de kg para toneladas para exibição
  const totalCO2Toneladas = totalCO2Kg / 1000;

  // Calcular métricas finais
  // Para mudas: baseado em R$ 13,35 = 1 muda (do Fundo de Mudas)
  // João: R$ 6,00 = 1 muda (baseado no valor atualizado)
  let totalMudas = 0;
  if (isAviation) {
    companyClientIds.forEach(clientId => {
      const clientData = clientsData[clientId];
      if (clientData && clientData.viagens) {
        clientData.viagens.forEach((viagem: any) => {
          if (viagem.fundoMudas) {
            totalMudas += Math.ceil(viagem.fundoMudas / 13.35);
          }
        });
      }
    });
  } else {
    totalMudas = Math.ceil(totalCO2Toneladas / 0.15);
  }
  
  const dashboardMetrics = {
    clientesAtivos: clients.filter(c => c.status === "Ativo").length,
    totalViagens: isAviation ? totalViagens : totalCheckIns,
    totalDistancia: totalDistancia,
    totalCO2: totalCO2Toneladas, // em toneladas
    totalMudas: totalMudas,
    totalCashbacksGerados: totalCashbacksGerados
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {company.logo && (
                <img 
                  src={company.logo} 
                  alt={`Logo ${company.nomeFantasia}`}
                  className="h-16 w-auto object-contain"
                />
              )}
              <CardTitle className="text-2xl font-bold">{company.razaoSocial}</CardTitle>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(-1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setIsManualModalOpen(true)}
                className="border-[#0e7cb4] text-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
              >
                <FileText className="mr-2 h-4 w-4" />
                Manual de Integração
              </Button>
              <Button className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                <Edit className="mr-2 h-4 w-4" />
                Editar
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Mini Dashboard - KPIs */}
          <div className="mb-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-700">Painel de Gestão da Parceria</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Card 1: Clientes Ativos */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Clientes Ativos</div>
                      <div className="text-3xl font-bold">{dashboardMetrics.clientesAtivos}</div>
                    </div>
                    <Users className="h-8 w-8 text-[#0e7cb4] opacity-50" />
                  </div>
                </CardContent>
              </Card>

              {/* Card 2: Viagens/Check-ins */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">
                        {isAviation ? "Viagens" : "Check-ins"}
                      </div>
                      <div className="text-3xl font-bold">{dashboardMetrics.totalViagens}</div>
                    </div>
                    {isAviation ? (
                      <Plane className="h-8 w-8 text-[#0e7cb4] opacity-50" />
                    ) : (
                      <Hotel className="h-8 w-8 text-[#0e7cb4] opacity-50" />
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Card 3: Distância Total */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Distância total (km)</div>
                      <div className="text-3xl font-bold">{dashboardMetrics.totalDistancia.toLocaleString('pt-BR')}</div>
                    </div>
                    <TrendingUp className="h-8 w-8 text-[#0e7cb4] opacity-50" />
                  </div>
                </CardContent>
              </Card>

              {/* Card 4: Emissões CO2e */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Emissões Compensadas (kgCO₂e)</div>
                      <div className="text-3xl font-bold">{dashboardMetrics.totalCO2.toFixed(2).replace(".", ",")} t</div>
                    </div>
                    <Cloud className="h-8 w-8 text-[#0e7cb4] opacity-50" />
                  </div>
                </CardContent>
              </Card>

              {/* Card 5: Mudas Financiadas */}
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Mudas Financiadas</div>
                      <div className="text-3xl font-bold text-green-600">
                        {mudasApoiadas} {mudasApoiadas === 1 ? 'Árvore' : 'Árvores'}
                      </div>
                    </div>
                    <Sprout className="h-8 w-8 text-green-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>

              {/* Card 6: Total de Cashbacks gerados */}
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Total de Cashbacks gerados</div>
                      <div className="text-3xl font-bold text-green-600">
                        {dashboardMetrics.totalCashbacksGerados.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </div>
                    </div>
                    <Wallet className="h-8 w-8 text-green-600 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList>
              <TabsTrigger value="dados-cadastrais">Dados Cadastrais</TabsTrigger>
              <TabsTrigger value="clientes">Clientes</TabsTrigger>
            </TabsList>

            <TabsContent value="dados-cadastrais" className="space-y-4 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Coluna Esquerda */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">CNPJ</label>
                    <Input value={company.cnpj} readOnly className="bg-gray-50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome Fantasia</label>
                    <Input value={company.nomeFantasia} readOnly className="bg-gray-50" />
                  </div>
                </div>

                {/* Coluna Direita */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Razão Social</label>
                    <Input value={company.razaoSocial} readOnly className="bg-gray-50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Ramo do Negócio</label>
                    <Input value={company.ramoNegocio} readOnly className="bg-gray-50" />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="clientes" className="space-y-4 pt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="h-5 w-5 text-[#0e7cb4]" />
                  <h2 className="text-xl font-bold">Clientes</h2>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <Input
                    className="pl-10"
                    placeholder="Buscar por nome, CPF ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome Completo</TableHead>
                        <TableHead>CPF</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Localização</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Data Cadastro</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredClients.length > 0 ? (
                        filteredClients.map((client) => (
                          <TableRow key={client.id}>
                            <TableCell className="font-medium">{client.nomeCompleto}</TableCell>
                            <TableCell>{client.cpf}</TableCell>
                            <TableCell>{client.email}</TableCell>
                            <TableCell>{client.telefone}</TableCell>
                            <TableCell>{client.localizacao}</TableCell>
                            <TableCell>{client.tipo}</TableCell>
                            <TableCell>{client.dataCadastro}</TableCell>
                            <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/clients/${client.id}`, {
                                state: { 
                                  empresaTipo: company.ramoNegocio,
                                  companyId: company.id
                                }
                              })}
                              className="text-[#0e7cb4] border-[#0e7cb4] hover:bg-[#0e7cb4] hover:text-white"
                            >
                              Ver Detalhes
                            </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                            Nenhum cliente encontrado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal de Registro de Check-in */}
      <Dialog open={isCheckInModalOpen} onOpenChange={setIsCheckInModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registrar Check-in Sustentável</DialogTitle>
            <DialogDescription>
              Registre o check-in de um hóspede e calcule a compensação de carbono do deslocamento.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCheckInSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="cliente">Cliente *</Label>
                <Select 
                  value={checkInForm.clienteId} 
                  onValueChange={(value) => setCheckInForm({...checkInForm, clienteId: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.nomeCompleto} - {client.cpf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cidadeOrigem">Cidade de Origem *</Label>
                <Input
                  id="cidadeOrigem"
                  placeholder="Ex: São Paulo, SP"
                  value={checkInForm.cidadeOrigem}
                  onChange={(e) => setCheckInForm({...checkInForm, cidadeOrigem: e.target.value})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="meioTransporte">Meio de Transporte *</Label>
                <Select 
                  value={checkInForm.meioTransporte} 
                  onValueChange={(value) => setCheckInForm({...checkInForm, meioTransporte: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o meio de transporte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Carro">Carro</SelectItem>
                    <SelectItem value="Ônibus">Ônibus</SelectItem>
                    <SelectItem value="Aéreo Convencional">Aéreo Convencional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="distancia">Distância Estimada (km) *</Label>
                <Input
                  id="distancia"
                  type="number"
                  step="0.1"
                  min="0"
                  placeholder="Ex: 350.5"
                  value={checkInForm.distancia}
                  onChange={(e) => setCheckInForm({...checkInForm, distancia: e.target.value})}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  A distância será usada para calcular a emissão de CO₂ e a compensação em Moedas.
                </p>
              </div>

              {checkInForm.distancia && checkInForm.meioTransporte && parseFloat(checkInForm.distancia) > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-sm mb-2">Resumo da Compensação:</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Emissão estimada:</span>
                      <span className="font-medium">
                        {calcularEmissaoCO2(parseFloat(checkInForm.distancia), checkInForm.meioTransporte).toFixed(3)} kg CO₂
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Moedas a serem creditadas:</span>
                      <span className="font-medium text-[#0e7cb4]">
                        {calcularMoedas(calcularEmissaoCO2(parseFloat(checkInForm.distancia), checkInForm.meioTransporte)).toFixed(2)} Moedas
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCheckInModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]">
                <CheckCircle className="mr-2 h-4 w-4" />
                Registrar Check-in
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Manual de Integração */}
      <Dialog open={isManualModalOpen} onOpenChange={setIsManualModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manual de Integração Técnica - API v1.0</DialogTitle>
            <DialogDescription>
              Documentação técnica para integração com a API do Banclima
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* 1. Autenticação */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">1. Autenticação</h3>
              <p className="text-sm text-gray-700">
                O parceiro deve usar o <strong>Client ID</strong> e <strong>Client Secret</strong> gerados no cadastro.
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <code className="text-sm text-gray-800">
                  Header: <span className="text-blue-600">Authorization: Bearer {'{CLIENT_SECRET}'}</span>
                </code>
              </div>
            </div>

            {/* 2. Endpoint */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">2. Endpoint de Envio</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <code className="text-sm text-gray-800">
                  <span className="text-green-600 font-semibold">POST</span> https://api.banclima.com.br/v1/integrations/flight-emission
                </code>
              </div>
            </div>

            {/* 3. Payload */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">3. Payload Obrigatório (JSON)</h3>
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-100">
{`{
  "transaction_id": "TKT-987654",
  "issue_date": "2025-12-20T14:30:00Z",
  "customer": {
    "cpf": "123.456.789-00",
    "email": "joao.silva@email.com"
  },
  "financial": {
    "currency": "BRL",
    "total_amount": 1500.00,
    "fare_class": "executive"
  },
  "trip": {
    "origin_iata": "SDU",
    "destination_iata": "GRU",
    "flight_number": "LA3456",
    "aircraft_model": "A320",
    "distance_km": 366,
    "stops": 0,
    "saf_data": {
      "used_saf": true,
      "blend_percentage": 25.0
    }
  }
}`}
                </pre>
              </div>
            </div>

            {/* 4. Regras Importantes */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">4. Regras Importantes</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  <div>
                    <strong>Stops {'>'} 0:</strong> Aplica penalidade de LTO (+15kg CO₂ por conexão).
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">•</span>
                  <div>
                    <strong>SAF (Sustainable Aviation Fuel):</strong> Aplica desconto proporcional na emissão baseado no <code className="bg-gray-100 px-1 rounded">blend_percentage</code>.
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <div>
                    <strong>Cashback Regressivo:</strong> O percentual de cashback é calculado automaticamente baseado no <code className="bg-gray-100 px-1 rounded">total_amount</code>:
                    <ul className="list-disc list-inside mt-1 ml-4 space-y-1 text-xs">
                      <li>R$ 0 a 500: 4.0%</li>
                      <li>R$ 501 a 1000: 3.5%</li>
                      <li>R$ 1001 a 2500: 3.0%</li>
                      <li>R$ 2501 a 5000: 2.5%</li>
                      <li>Acima de R$ 5000: 2.0%</li>
                    </ul>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <div>
                    <strong>Distribuição Financeira (Split):</strong> Do cashback total gerado:
                    <ul className="list-disc list-inside mt-1 ml-4 space-y-1 text-xs">
                      <li>0.89% → Fundo de Mudas</li>
                      <li>0.89% → Projetos Sociais</li>
                      <li>Restante → Saldo do Cliente</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Resposta da API */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-gray-800">5. Resposta da API</h3>
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-100">
{`{
  "success": true,
  "transaction_id": "TKT-987654",
  "banclima_transaction_id": "BLC-TXN-123456",
  "calculation": {
    "co2_kg": 60.0,
    "cashback_percentage": 3.0,
    "cashback_total": 45.00,
    "split": {
      "fundo_mudas": 13.35,
      "projetos_sociais": 13.35,
      "saldo_cliente": 18.30
    }
  },
  "customer_msc_balance": 18.30
}`}
                </pre>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                const docUrl = `${window.location.origin}/docs/api-aviation-integration`;
                copyToClipboard(docUrl, "Link da documentação");
              }}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copiar Link da Doc
            </Button>
            <Button 
              variant="outline"
              onClick={() => setIsManualModalOpen(false)}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetail;
